import uuid from 'react-uuid';
import { InvoiceItems } from 'src/types/Invoices';
import { IPostBody } from 'src/types/PostBody';
import axios from 'src/utils/axios';

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from 'src/app/store';
type InvoiceAPI = {
  companyId: string;
  body: IPostBody;
};

type CheckPreviousLinkParams = {
  companyId: string;
  body: Record<string, any>;
};

type LinkInvoiceParams = {
  receiptId: string | null;
  invoiceId: string;
};

type CreateInvoiceBody = {
  companyID: string | null;
  poId: string | null | number;
};

export const createInvoice = createAsyncThunk(
  'InvoiceItems/createInvoice',
  async (body: CreateInvoiceBody) => {
    const response = await axios.post(`/Invoice`, body);
    return (await response).data;
  }
);

export const fetchInvoiceData = createAsyncThunk(
  'InvoiceItems/fetchInvoiceData',
  async ({ companyId, body }: InvoiceAPI) => {
    const response = await axios.post(`/Invoice/Filter?companyId=${companyId}`, body);
    return (await response).data;
  }
);

export const checkIfLinkedToAnotherReceipt = createAsyncThunk(
  'InvoiceItems/checkIfLinedToAnotherReceipt',
  async ({ companyId, body }: CheckPreviousLinkParams) => {
    const response = axios.post(`/Receipt/Filter?companyId=${companyId}`, body);
    return (await response).data;
  }
);

export const linkInvoiceToReceipt = createAsyncThunk(
  'InvoiceItems/linkInvoiceToReceipt',
  async ({ receiptId, invoiceId }: LinkInvoiceParams) => {
    const response = axios.put(`/Receipt/${receiptId}/LinkInvoice?invoiceId=${invoiceId}`);
    return (await response).data;
  }
);

interface invoiceDataState {
  invoiceItems: {
    data: InvoiceItems[];
    total: number;
  };
  createInvoiceResponse: {
    receiptId: string;
  };
  invoiceLoading: 'loading' | 'pending' | 'succeeded' | 'failed';
  createInvoiceLoading: 'loading' | 'pending' | 'succeeded' | 'failed';
}

const initialState = {
  invoiceItems: { data: [], total: 0 },
  createInvoiceResponse: { receiptId: '' },
  invoiceLoading: 'loading',
  createInvoiceLoading: 'loading',
} as invoiceDataState;

export const invoiceGridSlice = createSlice({
  name: 'InvoiceItems',
  initialState,
  reducers: {
    // standard reducer logic, with auto-generated action types per reducer
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchInvoiceData.fulfilled,
        (state, action: PayloadAction<{ items: InvoiceItems[]; totalRecords: number }>) => {
          state.invoiceItems = { data: action.payload.items, total: action.payload.totalRecords };
          state.invoiceLoading = 'succeeded';
        }
      )
      .addCase(fetchInvoiceData.rejected, (state) => {
        state.invoiceLoading = 'failed';
      })
      .addCase(fetchInvoiceData.pending, (state) => {
        state.invoiceLoading = 'pending';
      })
      .addCase(
        checkIfLinkedToAnotherReceipt.fulfilled,
        (state, action: PayloadAction<{ items: InvoiceItems[]; totalRecords: number }>) => {
          state.invoiceLoading = 'succeeded';
        }
      )
      .addCase(checkIfLinkedToAnotherReceipt.rejected, (state) => {
        state.invoiceLoading = 'failed';
      })
      .addCase(checkIfLinkedToAnotherReceipt.pending, (state) => {
        state.invoiceLoading = 'pending';
      })
      .addCase(
        linkInvoiceToReceipt.fulfilled,
        (state, action: PayloadAction<{ items: InvoiceItems[]; totalRecords: number }>) => {
          state.invoiceLoading = 'succeeded';
        }
      )
      .addCase(linkInvoiceToReceipt.rejected, (state) => {
        state.invoiceLoading = 'failed';
      })
      .addCase(linkInvoiceToReceipt.pending, (state) => {
        state.invoiceLoading = 'pending';
      })
      .addCase(createInvoice.fulfilled, (state, action: PayloadAction<{ receiptId: string }>) => {
        state.createInvoiceResponse = { receiptId: action.payload?.receiptId };
        state.createInvoiceLoading = 'succeeded';
      })
      .addCase(createInvoice.rejected, (state) => {
        state.createInvoiceLoading = 'failed';
      })
      .addCase(createInvoice.pending, (state) => {
        state.createInvoiceLoading = 'pending';
      });
  },
});

export const selectInvoiceItems = (state: RootState) => state.invoiceItems;
export default invoiceGridSlice.reducer;

export const selectInvoiceUse = (state: RootState) => {
  const invoices: invoiceDataState = state.invoiceItems;
  return {
    ...invoices,
    invoiceItems: {
      data: invoices.invoiceItems.data.map((invoice: InvoiceItems) => ({
        ...invoice,
        rowIdentifier: uuid(),
      })),
      total: invoices.invoiceItems.total,
    },
  };
};
